import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { Menu } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ImpersonationBanner from '../ImpersonationBanner';
import LanguageSwitcher from '../LanguageSwitcher';
import NotificationBell from './NotificationBell';
import { hasUserPermission, PERMISSIONS } from 'utils/permissionUtils';
import AppContext from 'contexts/AppContext';
import { matchNavEntry, trailingSegments } from './navigation';

export interface AppTopbarProps {
  /** Opens the sidebar drawer. Renders the hamburger below the drawer breakpoint when provided. */
  onOpenNav?: () => void;
}

interface PageCrumb {
  label: string | null;
  setLabel: (label: string | null) => void;
}

// A no-op default rather than a throw: embedded mode renders no topbar, so a record page can
// call the hook with no provider above it.
const PageCrumbContext = createContext<PageCrumb>({ label: null, setLabel: () => {} });

/**
 * Lets the page name the last breadcrumb segment: `trailingSegments` only ever sees the URL, so
 * a record page renders its raw id forever. A context, not a prop — the shell renders the topbar
 * as a sibling of the page, so a prop would have to thread through `AdminLayout`.
 */
export const PageCrumbProvider = ({ children }: { children: ReactNode }) => {
  const [label, setLabel] = useState<string | null>(null);
  const value = useMemo(() => ({ label, setLabel }), [label]);

  return <PageCrumbContext.Provider value={value}>{children}</PageCrumbContext.Provider>;
};

/**
 * Announces this page's crumb. Pass nothing while the record loads: the breadcrumb then keeps
 * the raw segment instead of flashing empty.
 */
export const usePageCrumb = (label?: string | null): void => {
  const { setLabel } = useContext(PageCrumbContext);

  useEffect(() => {
    setLabel(label ?? null);
    return () => setLabel(null); // the next page must never inherit this one's name
  }, [label, setLabel]);
};

const AppTopbar = ({ onOpenNav }: AppTopbarProps) => {
  const { currentUser } = useContext(AppContext) as { currentUser: unknown };
  const canReadTechnicians = hasUserPermission(currentUser, PERMISSIONS.DASHBOARD_TECHNICIANS_VIEW);
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { label } = useContext(PageCrumbContext);
  const entry = matchNavEntry(pathname);
  const segments = entry ? trailingSegments(pathname, entry) : [];
  // The supplied label replaces the last segment only; its parents stay what the URL says.
  const trail = label && segments.length ? [...segments.slice(0, -1), label] : segments;
  const crumbs: { text: string; to?: string }[] = [
    { text: t('Administration') },
    ...(entry
      ? [
          // Named crumb below it means the section is a place to go back to, so it becomes a link.
          { text: entry.label(t), to: label && trail.length ? entry.to : undefined },
          ...trail.map((text) => ({ text })),
        ]
      : []),
  ];

  return (
    <header className="sticky top-0 z-20 flex h-14 shrink-0 items-center gap-3 border-b border-zinc-200 bg-white px-4 dark:border-zinc-800 dark:bg-zinc-950">
      {onOpenNav && (
        <button
          type="button"
          onClick={onOpenNav}
          aria-label={t('Open navigation')}
          className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-lg text-zinc-500 transition-colors hover:bg-zinc-100 hover:text-zinc-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900 dark:text-zinc-400 dark:hover:bg-zinc-800 dark:hover:text-zinc-50 dark:focus-visible:ring-zinc-100 lg:hidden"
        >
          <Menu className="h-[18px] w-[18px]" aria-hidden="true" />
        </button>
      )}

      <nav aria-label={t('Breadcrumb')} className="min-w-0 flex-1">
        <ol className="flex min-w-0 items-center gap-1.5 text-sm">
          {crumbs.map((crumb, index) => (
            <li key={`${crumb.text}-${index}`} className="flex min-w-0 items-center gap-1.5">
              {index > 0 && (
                <span aria-hidden="true" className="text-zinc-300 dark:text-zinc-600">
                  ›
                </span>
              )}
              {crumb.to ? (
                <Link
                  to={crumb.to}
                  className="truncate text-zinc-500 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-50"
                >
                  {crumb.text}
                </Link>
              ) : (
                <span
                  className={
                    index === crumbs.length - 1
                      ? 'truncate font-medium text-zinc-900 dark:text-zinc-50'
                      : 'truncate text-zinc-500 dark:text-zinc-400'
                  }
                >
                  {crumb.text}
                </span>
              )}
            </li>
          ))}
        </ol>
      </nav>

      <div className="flex shrink-0 items-center gap-3">
        <ImpersonationBanner />
        {/* One bell, and it is the first thing on the right: what came back from a technician is
            the only thing on this bar that decays if nobody looks (D41). Rendered only for an
            account that can open the screen it leads to — the feed carries the same data, and the
            service refuses it to anyone else anyway. */}
        {canReadTechnicians && <NotificationBell />}
        <LanguageSwitcher onDark={false} />
      </div>
    </header>
  );
};

export default AppTopbar;
